import { useEffect, useState, useMemo } from 'react';
import { useMutation, useQuery } from 'data-fetching-client';
import { useDispatch } from 'react-redux';
import sortBy from 'hs-lodash/sortBy';
import isEqual from 'hs-lodash/isEqual';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { PLATFORM_NOTIFICATION_SETTINGS } from 'automation-ui-usage-tracking/UsageTrackingSubscreens';
import { GET_WORFKLOW_NOTIFICATIONS_SUBSCRIPTIONS_SETTINGS, UPDATE_WORKFLOW_NOTIFICATIONS_SUBSCRIPTIONS_SETTINGS } from 'automation-ui-canvas/queries/notificationSettingsQueries';
import { NotificationTypes } from 'automation-ui-canvas/constants/NotificationTypes';
import { addAlert } from 'automation-ui-canvas/actions/MessageActions';
import * as AlertTypes from 'automation-ui-canvas/constants/AlertTypes';
import makeDefaultNotificationConfigsMap from 'automation-ui-canvas/containers/settings/utils/makeDefaultNotificationConfigsMap';
import { getEnabledSettingsForTracking } from 'automation-ui-canvas/containers/settings/utils/notificationSettingsTracking';
import { jsx as _jsx } from "react/jsx-runtime";
export const subscriptionSettingsRequestToFormAdapter = settings => {
  const updateNotificationConfig = (notificationConfigMap, key, value) => {
    notificationConfigMap[key] = value;
  };
  const {
    subscribedUserIds,
    subscribedTeamIds,
    notificationConfigs
  } = settings;
  const notificationConfigsMap = makeDefaultNotificationConfigsMap();
  notificationConfigs.forEach(config => {
    updateNotificationConfig(notificationConfigsMap, config.type, config);
  });
  return {
    // dev note (SE) - sort when writing to simplify dirty checks
    subscribedUserIds: sortBy(subscribedUserIds.map(id => String(id))),
    subscribedTeamIds: sortBy(subscribedTeamIds),
    notificationConfigs: notificationConfigsMap
  };
};
export const subscriptionSettingsFormToRequestAdapter = settings => {
  const {
    subscribedUserIds,
    subscribedTeamIds,
    notificationConfigs
  } = settings;
  return {
    subscribedUserIds: subscribedUserIds.map(id => Number(id)),
    subscribedTeamIds,
    notificationConfigs: Object.values(notificationConfigs)
  };
};
const WorkflowNotificationSettingsDecorator = WrappedComponent => {
  return function WithWrapper(props) {
    const dispatch = useDispatch();
    const trackEvent = useTrackEvent();

    // Form state
    const [notificationsSubscriptionsSettings, setNotificationsSubscriptionsSettings] = useState({
      subscribedTeamIds: [],
      subscribedUserIds: [],
      notificationConfigs: makeDefaultNotificationConfigsMap()
    });

    // Fetch settings
    const {
      data: notificationsSubsrciptionsSettingsData
    } = useQuery(GET_WORFKLOW_NOTIFICATIONS_SUBSCRIPTIONS_SETTINGS, {
      variables: {
        flowId: props.flowId
      }
    });
    const savedNotificationsSubscriptionsSettings = useMemo(() => {
      return notificationsSubsrciptionsSettingsData && subscriptionSettingsRequestToFormAdapter(notificationsSubsrciptionsSettingsData.workflowNotificationsSubscriptionsSettings);
    }, [notificationsSubsrciptionsSettingsData]);
    const lastUpdatedAt = notificationsSubsrciptionsSettingsData && notificationsSubsrciptionsSettingsData.workflowNotificationsSubscriptionsSettings.lastUpdatedAt || 0;

    // Update settings
    const [updateWorkflowNotificationsSubscriptionsSettings, {
      loading: notificationsSubscriptionsSettingsIsSaving
    }] = useMutation(UPDATE_WORKFLOW_NOTIFICATIONS_SUBSCRIPTIONS_SETTINGS, {
      onCompleted: () => {
        dispatch(addAlert(AlertTypes.WORKFLOW_NOTIFICATIONS_SETTINGS_SAVING_SUCCESS));
      },
      onError: () => {
        dispatch(addAlert(AlertTypes.WORKFLOW_NOTIFICATIONS_SETTINGS_SAVING_FAIL));
      },
      refetchQueries: [GET_WORFKLOW_NOTIFICATIONS_SUBSCRIPTIONS_SETTINGS]
    });
    useEffect(() => {
      if (savedNotificationsSubscriptionsSettings) {
        setNotificationsSubscriptionsSettings(savedNotificationsSubscriptionsSettings);
      }
    }, [savedNotificationsSubscriptionsSettings]);

    // Dirty checks
    const hasNotificationsSubscriptionsSettingsChanges = useMemo(() => {
      return !!savedNotificationsSubscriptionsSettings && !isEqual(savedNotificationsSubscriptionsSettings, notificationsSubscriptionsSettings);
    }, [savedNotificationsSubscriptionsSettings, notificationsSubscriptionsSettings]);
    const hasNotificationChanges = hasNotificationsSubscriptionsSettingsChanges;

    // Submit/clear handlers
    const trackSave = () => {
      const {
        subscribedUserIds,
        subscribedTeamIds,
        notificationConfigs
      } = notificationsSubscriptionsSettings;
      const {
        ['ENROLLMENT_DIGEST']: {
          rule: {
            increaseThresholdPercentage,
            decreaseThresholdPercentage,
            notificationCadence
          }
        }
      } = notificationConfigs;
      trackEvent(Trackables.SAVED_ENROLLMENT_NOTIFICATION_SETTINGS, {
        subscreen: PLATFORM_NOTIFICATION_SETTINGS,
        hasIncreaseThresholdSet: !isNaN(increaseThresholdPercentage),
        hasDecreaseThresholdSet: !isNaN(decreaseThresholdPercentage),
        hasUsersSet: subscribedUserIds.length >= 1,
        hasTeamsSet: subscribedTeamIds.length >= 1,
        notificationCadence,
        type: getEnabledSettingsForTracking(notificationConfigs)
      });
    };
    const onNotificationsSettingsSave = () => {
      if (hasNotificationChanges) {
        trackSave();
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        updateWorkflowNotificationsSubscriptionsSettings({
          variables: Object.assign({
            flowId: props.flowId,
            outdatedIfLastUpdatedMoreRecentThan: lastUpdatedAt
          }, subscriptionSettingsFormToRequestAdapter(notificationsSubscriptionsSettings))
        });
      }
    };
    const onNotificationsSettingsClear = () => {
      if (hasNotificationChanges) {
        setNotificationsSubscriptionsSettings(savedNotificationsSubscriptionsSettings);
      }
    };
    const injectedProps = {
      hasNotificationChanges,
      notificationsSubscriptionsSettings,
      setNotificationsSubscriptionsSettings,
      notificationsSettingsIsSaving: notificationsSubscriptionsSettingsIsSaving,
      onNotificationsSettingsClear,
      onNotificationsSettingsSave
    };
    return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props, injectedProps));
  };
};
export default WorkflowNotificationSettingsDecorator;